import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useUser } from "@/contexts/user-provider";

import { parseError } from "@/lib/parse-error";
import { toast } from "@/lib/use-toast";

import {
  ClockInLog,
  ClockInRecord,
  ClockInRecordUpdate,
} from "@/types/clock-in-record";

import { requestData } from "@/api/api-config";

export interface ClockInRecordCreate {
  userId: string;
  locationId: string;
  startTime: Date;
  endTime?: Date;
  timezone: string;
}

async function getTimeEntry(): Promise<ClockInRecord | null> {
  return await requestData<ClockInRecord>(
    "check-in/clock-in",
    "get",
    undefined,
    false,
  );
}

async function listTimeEntries(opts: {
  start: Date;
  end: Date;
  userIds?: string[];
  locationIds?: string[];
}): Promise<ClockInRecord[] | null> {
  const { start, end, userIds, locationIds } = opts;
  return await requestData<ClockInRecord[]>(
    "check-in/clock-in/history",
    "get",
    {
      searchParams: {
        start: start.toISOString(),
        end: end.toISOString(),
        userIds,
        locationIds,
      },
    },
  );
}

async function clockIn(locationId: string): Promise<ClockInRecord | null> {
  return await requestData<ClockInRecord>(
    "check-in/clock-in",
    "post",
    { json: { location_id: locationId } },
    false,
  );
}

async function clockOut(): Promise<ClockInRecord | null> {
  return await requestData<ClockInRecord>(
    "check-in/clock-in/clock-out",
    "post",
    undefined,
    false,
  );
}

async function updateTimeEntry(
  timeEntryId: string,
  update: ClockInRecordUpdate,
): Promise<ClockInRecord | null> {
  return await requestData<ClockInRecord>(
    `check-in/clock-in/${timeEntryId}`,
    "put",
    {
      json: {
        start_time: update.startTime?.toISOString(),
        // Omitted keys are left unchanged by the API; null clears the clock-out.
        end_time:
          update.endTime === undefined
            ? undefined
            : (update.endTime?.toISOString() ?? null),
        reason: update.reason,
        timezone: update.timezone,
        location_id: update.locationId,
      },
    },
    false,
  );
}

async function getClockInLogs(
  timeEntryId: string,
): Promise<ClockInLog[] | null> {
  return await requestData<ClockInLog[]>(
    `check-in/clock-in/${timeEntryId}/logs`,
    "get",
    undefined,
    false,
  );
}

async function deleteTimeEntry(
  timeEntryId: string,
  reason?: string,
): Promise<void> {
  await requestData<void>(
    `check-in/clock-in/${timeEntryId}`,
    "delete",
    reason ? { json: { reason } } : undefined,
    false,
  );
}

async function createTimeEntry(
  data: ClockInRecordCreate,
): Promise<ClockInRecord | null> {
  return await requestData<ClockInRecord>(
    "check-in/clock-in/create",
    "post",
    {
      json: {
        user_id: data.userId,
        location_id: data.locationId,
        start_time: data.startTime.toISOString(),
        end_time: data.endTime ? data.endTime.toISOString() : null,
        timezone: data.timezone,
      },
    },
    false,
  );
}

export const clockInQueryKeys = {
  all: ["check-in", "clock-in"] as const,
  current: () => [...clockInQueryKeys.all, "current"] as const,
  list: (opts: {
    start: Date;
    end: Date;
    userIds?: string[];
    locationIds?: string[];
  }) => [...clockInQueryKeys.all, "list", opts] as const,
  logs: (timeEntryId: string) =>
    [...clockInQueryKeys.all, "logs", timeEntryId] as const,
  // History page query keys
  flat: <T>(filters: T, page: number, perPage: number) =>
    [...clockInQueryKeys.all, "flat", filters, page, perPage] as const,
  summary: <T>(filters: T) =>
    [...clockInQueryKeys.all, "summary", filters] as const,
  groups: <K, T>(paramKey: K, baseFilters: T) =>
    [...clockInQueryKeys.all, "groups", paramKey, baseFilters] as const,
  history: <T>(filtersWithGroup: T) =>
    [...clockInQueryKeys.all, "history", filtersWithGroup] as const,
};

export const clockInQueryOptions = {
  current: () => ({
    queryKey: clockInQueryKeys.current(),
    queryFn: () => getTimeEntry(),
  }),
  list: (opts: {
    start: Date;
    end: Date;
    userIds?: string[];
    locationIds?: string[];
  }) => ({
    queryKey: clockInQueryKeys.list(opts),
    queryFn: () => listTimeEntries(opts),
  }),
  logs: (timeEntryId: string) => ({
    queryKey: clockInQueryKeys.logs(timeEntryId),
    queryFn: () => getClockInLogs(timeEntryId),
  }),
};

export const useClockInMutation = () => {
  const queryClient = useQueryClient();
  const { currentUser } = useUser();

  return useMutation({
    mutationFn: (locationId: string) => clockIn(locationId),
    onMutate: async (locationId: string) => {
      await queryClient.cancelQueries({ queryKey: clockInQueryKeys.all });

      const currentKey = clockInQueryKeys.current();
      const previousTimeEntry = queryClient.getQueryData<ClockInRecord | null>(
        currentKey,
      );

      // Optimistically create a new time entry
      const optimisticTimeEntry: ClockInRecord = {
        id: "", // Will be set by server
        userId: currentUser?.id ?? "", // Will be set by server
        startTime: new Date().toISOString(),
        locationId: locationId,
      };

      queryClient.setQueryData<ClockInRecord | null>(
        currentKey,
        optimisticTimeEntry,
      );

      return { previousTimeEntry };
    },
    onError: async (err, _vars, context) => {
      if (context?.previousTimeEntry !== undefined) {
        queryClient.setQueryData(
          clockInQueryKeys.current(),
          context.previousTimeEntry,
        );
      }
      // Surface the backend detail (e.g. clock-in network restriction) instead of
      // a generic message.
      const description = await parseError(
        err,
        "Failed to clock in. Please try again.",
      );
      toast({
        title: "Error",
        description,
        variant: "error",
      });
    },
    onSuccess: (data) => {
      if (data) {
        queryClient.setQueryData(clockInQueryKeys.current(), data);
      }
    },
  });
};

export const useClockOutMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => clockOut(),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: clockInQueryKeys.all });

      const currentKey = clockInQueryKeys.current();
      const previousTimeEntry = queryClient.getQueryData<ClockInRecord | null>(
        currentKey,
      );

      // Optimistically update the current time entry with end time
      if (previousTimeEntry) {
        const optimisticTimeEntry: ClockInRecord = {
          ...previousTimeEntry,
          endTime: new Date().toISOString(),
        };
        queryClient.setQueryData<ClockInRecord | null>(
          currentKey,
          optimisticTimeEntry,
        );
      }

      return { previousTimeEntry };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousTimeEntry !== undefined) {
        queryClient.setQueryData(
          clockInQueryKeys.current(),
          context.previousTimeEntry,
        );
      }
      toast({
        title: "Error",
        description: "Failed to clock out. Please try again.",
        variant: "error",
      });
    },
    onSuccess: (data) => {
      if (data) {
        queryClient.setQueryData(clockInQueryKeys.current(), data);
      }
    },
  });
};

export const useUpdateTimeEntryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      timeEntryId,
      update,
    }: {
      timeEntryId: string;
      update: ClockInRecordUpdate;
    }) => updateTimeEntry(timeEntryId, update),
    onMutate: async ({ timeEntryId, update }) => {
      await queryClient.cancelQueries({ queryKey: clockInQueryKeys.all });

      // Find and update the time entry optimistically in all list queries
      const listQueries = queryClient
        .getQueryCache()
        .findAll({ queryKey: clockInQueryKeys.all });

      const previousData: Array<{
        queryKey: readonly unknown[];
        data: ClockInRecord[] | ClockInRecord | null;
      }> = [];

      listQueries.forEach((query) => {
        const data = query.state.data;
        previousData.push({
          queryKey: query.queryKey,
          data: data as ClockInRecord[] | ClockInRecord | null,
        });

        if (Array.isArray(data)) {
          const updatedList = data.map((entry) => {
            if (entry.id === timeEntryId) {
              return {
                ...entry,
                ...(update.startTime && {
                  startTime: update.startTime.toISOString(),
                }),
                // Mirror the API: undefined leaves the clock-out, null clears it.
                ...(update.endTime !== undefined && {
                  endTime: update.endTime?.toISOString(),
                }),
                ...(update.locationId && { locationId: update.locationId }),
              };
            }
            return entry;
          });
          queryClient.setQueryData(query.queryKey, updatedList);
        }
      });

      return { previousData };
    },
    onError: (_err, _vars, context) => {
      // Revert optimistic updates
      if (context?.previousData) {
        context.previousData.forEach(({ queryKey, data }) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      toast({
        title: "Error",
        description: "Failed to update time entry. Please try again.",
        variant: "error",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: clockInQueryKeys.all });
    },
  });
};

export const useDeleteTimeEntryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      timeEntryId,
      reason,
    }: {
      timeEntryId: string;
      reason?: string;
    }) => deleteTimeEntry(timeEntryId, reason),
    onMutate: async ({ timeEntryId }) => {
      await queryClient.cancelQueries({ queryKey: clockInQueryKeys.all });

      // Find and remove the time entry optimistically from all list queries
      const listQueries = queryClient
        .getQueryCache()
        .findAll({ queryKey: clockInQueryKeys.all });

      const previousData: Array<{
        queryKey: readonly unknown[];
        data: ClockInRecord[] | ClockInRecord | null;
      }> = [];

      listQueries.forEach((query) => {
        const data = query.state.data;
        previousData.push({
          queryKey: query.queryKey,
          data: data as ClockInRecord[] | ClockInRecord | null,
        });

        if (Array.isArray(data)) {
          // Filter out the deleted entry
          const updatedList = data.filter((entry) => entry.id !== timeEntryId);
          queryClient.setQueryData(query.queryKey, updatedList);
        }
      });

      return { previousData };
    },
    onError: (_err, _vars, context) => {
      // Revert optimistic updates
      if (context?.previousData) {
        context.previousData.forEach(({ queryKey, data }) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      toast({
        title: "Error",
        description: "Failed to delete time entry. Please try again.",
        variant: "error",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: clockInQueryKeys.all });
    },
  });
};

export const useCreateTimeEntryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ClockInRecordCreate) => createTimeEntry(data),
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to create time entry. Please try again.",
        variant: "error",
      });
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Time entry created successfully.",
        variant: "success",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: clockInQueryKeys.all });
    },
  });
};
